import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Camera, X, ShoppingBag, Package, CurrencyDollar } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostMerchandise() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', price: '',
    condition: 'new', category: 'general', shipping_info: '', in_stock: true,
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center" data-testid="admin-only-message">
        <div className="text-center">
          <ShoppingBag size={48} weight="duotone" className="mx-auto text-[#5C7A99] mb-4" />
          <p className="text-lg font-bold text-[#004E89]">Admin Only</p>
          <p className="text-sm text-[#5C7A99]">Only administrators can post merchandise</p>
        </div>
      </div>
    );
  }

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({ file, preview: URL.createObjectURL(file), path: null }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const uploadPhotos = async () => {
    const uploaded = [];
    for (let i = 0; i < photos.length; i++) {
      if (photos[i].path) { uploaded.push(photos[i].path); continue; }
      const formData = new FormData();
      formData.append('file', photos[i].file);
      try {
        const { data } = await axios.post(`${API}/upload`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(data.path);
      } catch {
        toast.error(`Failed to upload ${photos[i].file.name}`);
      }
    }
    return uploaded;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) {
      toast.error('Title and price are required');
      return;
    }
    setLoading(true);
    try {
      const photoPaths = await uploadPhotos();
      await axios.post(`${API}/merchandise`, {
        ...form,
        price: parseFloat(form.price),
        photo_paths: photoPaths,
      }, { withCredentials: true });
      toast.success('Item posted to the shop!');
      navigate('/shop');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="post-merchandise-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>
          Post Merchandise
        </h1>
        <p className="text-[#5C7A99] mb-8">Add a promotional item to the shop</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Item Info */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <ShoppingBag size={24} weight="duotone" />
              Item Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Title *</label>
                <input value={form.title} onChange={(e) => updateField('title', e.target.value)}
                  className="input-neo" placeholder="e.g., Vintage Clock Set" required data-testid="merch-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Price *</label>
                  <div className="relative">
                    <CurrencyDollar size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
                    <input type="number" step="0.01" min="0" value={form.price}
                      onChange={(e) => updateField('price', e.target.value)}
                      className="input-neo pl-10" placeholder="29.99" required data-testid="merch-price-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Condition</label>
                  <Select value={form.condition} onValueChange={(v) => updateField('condition', v)}>
                    <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11 font-bold" data-testid="merch-condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="like_new">Like New</SelectItem>
                      <SelectItem value="used">Used</SelectItem>
                      <SelectItem value="refurbished">Refurbished</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Category</label>
                <Select value={form.category} onValueChange={(v) => updateField('category', v)}>
                  <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11 font-bold" data-testid="merch-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="clothing">Clothing</SelectItem>
                    <SelectItem value="accessories">Accessories</SelectItem>
                    <SelectItem value="home">Home & Decor</SelectItem>
                    <SelectItem value="collectibles">Collectibles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Description *</label>
                <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)}
                  className="input-neo min-h-[100px] resize-y" placeholder="Describe the item..." required data-testid="merch-description-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Shipping Info</label>
                <input value={form.shipping_info} onChange={(e) => updateField('shipping_info', e.target.value)}
                  className="input-neo" placeholder="e.g., Free shipping, Local pickup only" data-testid="merch-shipping-input" />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Camera size={24} weight="duotone" />
              Photos
            </h2>
            <div className="photo-upload-zone mb-4" onClick={() => fileInputRef.current?.click()} data-testid="merch-photo-upload">
              <Camera size={36} weight="duotone" className="mx-auto text-[#5C7A99] mb-2" />
              <p className="text-sm font-medium text-[#5C7A99]">Click to add photos</p>
            </div>
            <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotoSelect} className="hidden" data-testid="merch-photo-input" />
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden border-2 border-[#004E89] h-24">
                    <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-lg" data-testid="post-merch-submit">
            {loading ? 'Posting...' : 'Post to Shop'}
          </button>
        </form>
      </div>
    </div>
  );
}
